/**
 * `wikilinkAutocompleteExtension` — `[[title]]` decoration + live completion (editor-chat.md
 * §2.1, §2.2, §5 step 9).
 *
 * editor-chat.md §2.2: wikilink completion is triggered on `[[`, fuzzy-filters the cached
 * `AutocompleteSources.titles` by `title`, ranks by match quality then recency, and inserts
 * `[[title]]`. A wikilink whose target title does not exist is **still allowed** (PRD §8.3 —
 * unresolved wikilinks are deferred resolutions handled by ingestion); the editor may visually
 * distinguish unresolved links using the cached title set but never blocks the insert.
 *
 * Two parts, mirroring the hashtag extension:
 *  - a **decoration view plugin** that recognizes `[[...]]` tokens in the *visible viewport
 *    only* and marks them — resolved links get one class, unresolved another (the cached
 *    title set is the resolution oracle; an unknown title is decorated, never rejected);
 *  - a **completion source** that fuzzy-filters the cached `AutocompleteSourceStore.current()
 *    .titles` *locally* — at D-009's 100K-note scale the title list is a few MB in memory but
 *    keystroke filtering never awaits (editor-chat.md §2.2).
 */

import type { CompletionContext, CompletionResult, CompletionSource } from '@codemirror/autocomplete';
import type { EditorView } from '@codemirror/view';
import { Decoration, type DecorationSet, ViewPlugin, type ViewUpdate } from '@codemirror/view';
import { RangeSetBuilder, type Extension } from '@codemirror/state';
import type { AutocompleteSourceStore } from '../autocomplete/sources';
import type { TitleCompletionEntry } from '../editorTypes';

/** Recognizes a complete `[[title]]` token — the inner text is the wikilink target title. */
const WIKILINK_TOKEN = /\[\[([^\]\n]+)\]\]/g;

/**
 * Matches the *in-progress* wikilink immediately left of the cursor — `[[` followed by the
 * partial title typed so far (no closing `]]` yet). The captured group is the partial title.
 */
const WIKILINK_BEFORE_CURSOR = /\[\[([^\]\n]*)$/;

/** How many title completions to surface — the fuzzy match's top hits. */
const MAX_WIKILINK_COMPLETIONS = 20;

/** The CSS class for a `[[title]]` whose target exists in the cached title set. */
const WIKILINK_RESOLVED_CLASS = 'cm-aletheia-wikilink';

/** The CSS class for a `[[title]]` whose target is NOT in the cached title set — still valid. */
const WIKILINK_UNRESOLVED_CLASS = 'cm-aletheia-wikilink-unresolved';

// ---------------------------------------------------------------------------
// Decoration view plugin — viewport-scoped, resolved/unresolved distinction
// ---------------------------------------------------------------------------

/**
 * Build the wikilink decoration set for the *visible viewport only*. Each `[[title]]` token is
 * marked resolved or unresolved by checking the inner title against the cached title set —
 * an unresolved link is *decorated differently*, never rejected (PRD §8.3 — unresolved
 * wikilinks are legitimate deferred resolutions).
 *
 * Viewport-scoped, recomputed on doc/viewport change only (editor-chat.md §2.1 performance
 * contract).
 */
function buildWikilinkDecorations(view: EditorView, store: AutocompleteSourceStore): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  // A case-insensitive set of known titles — the resolution oracle. Rebuilt per decoration
  // pass; the title set changes only on a debounced refresh, and a pass only runs on
  // doc/viewport change, so this is not a per-keystroke cost.
  const knownTitles = new Set(store.current().titles.map((t) => t.title.toLowerCase()));
  const resolvedMark = Decoration.mark({ class: WIKILINK_RESOLVED_CLASS });
  const unresolvedMark = Decoration.mark({ class: WIKILINK_UNRESOLVED_CLASS });

  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    WIKILINK_TOKEN.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = WIKILINK_TOKEN.exec(text)) !== null) {
      const tokenStart = from + match.index;
      const tokenEnd = tokenStart + match[0].length;
      const innerTitle = match[1].trim().toLowerCase();
      builder.add(
        tokenStart,
        tokenEnd,
        knownTitles.has(innerTitle) ? resolvedMark : unresolvedMark,
      );
    }
  }
  return builder.finish();
}

/**
 * The `[[title]]` decoration view plugin, built over a captured store. Rebuilds on
 * construction and on doc/viewport change.
 */
function makeWikilinkDecorationPlugin(store: AutocompleteSourceStore) {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = buildWikilinkDecorations(view, store);
      }

      update(update: ViewUpdate): void {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = buildWikilinkDecorations(update.view, store);
        }
      }
    },
    { decorations: (plugin) => plugin.decorations },
  );
}

// ---------------------------------------------------------------------------
// Completion source — local fuzzy filter over the cached title set
// ---------------------------------------------------------------------------

/**
 * A cheap subsequence fuzzy match: every character of `needle` appears in `haystack` in order.
 * Returns a match-quality score (higher is better — a contiguous prefix match scores highest)
 * or `null` for no match. Used to fuzzy-rank titles for wikilink completion (editor-chat.md
 * §2.2 — "fuzzy-filters titles by title, ranked by match quality then recency").
 */
function fuzzyScore(needle: string, haystack: string): number | null {
  if (needle === '') return 0;
  const n = needle.toLowerCase();
  const h = haystack.toLowerCase();
  if (h.startsWith(n)) return 1000 - haystack.length; // a prefix match is the strongest signal
  let hi = 0;
  let score = 0;
  let lastHit = -2;
  for (let ni = 0; ni < n.length; ni += 1) {
    const found = h.indexOf(n[ni], hi);
    if (found === -1) return null;
    // Contiguous hits score higher than scattered ones.
    score += found === lastHit + 1 ? 5 : 1;
    lastHit = found;
    hi = found + 1;
  }
  return score;
}

/**
 * Build the wikilink completion source over a captured `AutocompleteSourceStore`.
 *
 * Triggered when the text left of the cursor matches `WIKILINK_BEFORE_CURSOR` (`[[` + a
 * partial title). It fuzzy-scores `store.current().titles` *synchronously*, ranks by score
 * (then leaves the store's order — which is `note_titles`' order — as the recency tiebreak),
 * and applies `title]]` (the `[[` is already typed; the completion closes the link). Returns
 * `null` outside a wikilink context.
 */
function makeWikilinkCompletionSource(store: AutocompleteSourceStore) {
  return (context: CompletionContext): CompletionResult | null => {
    const before = context.matchBefore(WIKILINK_BEFORE_CURSOR);
    if (before === null) return null;
    const partialMatch = WIKILINK_BEFORE_CURSOR.exec(before.text);
    const partial = partialMatch?.[1] ?? '';

    const titles = store.current().titles;
    const scored: { entry: TitleCompletionEntry; score: number }[] = [];
    for (const entry of titles) {
      const score = fuzzyScore(partial, entry.title);
      if (score !== null) scored.push({ entry, score });
    }
    scored.sort((a, b) => b.score - a.score);
    const matches = scored.slice(0, MAX_WIKILINK_COMPLETIONS);

    // `from` is the position just after `[[`, so applying replaces the partial title.
    const from = context.pos - partial.length;
    return {
      from,
      options: matches.map(({ entry }) => ({
        label: entry.title,
        type: 'text',
        // Close the wikilink on apply — the `[[` is already in the doc.
        apply: `${entry.title}]]`,
      })),
      validFor: /^[^\]\n]*$/,
    };
  };
}

// ---------------------------------------------------------------------------
// The composed extension
// ---------------------------------------------------------------------------

/**
 * The wikilink decoration extension — `[[title]]` viewport-scoped marker, resolved vs
 * unresolved styling (editor-chat.md §2.1).
 *
 * NOTE — completion source is exported separately via `wikilinkCompletionSource(store)`
 * and registered by `<EditorViewHost>` together with the hashtag source in a SINGLE
 * `autocompletion({ override: [...] })` call. See the symmetric note in
 * `hashtag-autocomplete.ext.ts` for the "Config merge conflict" reason.
 */
export function wikilinkAutocompleteExtension(store: AutocompleteSourceStore): Extension {
  return makeWikilinkDecorationPlugin(store);
}

/**
 * The wikilink completion source — for the editor's single combined `autocompletion()` call.
 * Self-scopes: returns `null` when the cursor is not inside `[[…`, so it coexists with the
 * hashtag source via CM6's standard "first non-null source wins" dispatch.
 */
export function wikilinkCompletionSource(store: AutocompleteSourceStore): CompletionSource {
  return makeWikilinkCompletionSource(store);
}
